
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, DiscountApplication, CartData, CartSnapshot, InteractionState } from '../types/cartTypes';
import type { AppError } from '../errors/types';
import { getCartPolicyConfigFromWindow, getHolidayConfigFromWindow, getFreeShippingThresholdFromWindow, DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS, type CartPolicyConfig, type HolidayConfig } from '../data/config';
import { reconcileCart } from '../utils/cartReconciliation';
import { recalcUiCartAfterQtyChange } from '../utils/cart/pricing';
import { getLineRef } from '../utils/cart/lineRef';

// 1. 定义数据结构
/**
 * 优惠券/折扣码状态
 */
interface DiscountCodeState {
  /** 优惠码文本 */
  code: string | null;
  /** 已应用的折扣码列表（用于多码回放与合并） */
  appliedCodes: string[];
  /** 是否有效（经过影子购物车验证） */
  isValid: boolean;
  /** 折扣金额 (分) */
  discountAmount: number;
  /** 错误信息 */
  error?: string | null;
  /** 是否正在进行后台验证 */
  isValidating: boolean;
}

/**
 * 购物车通知消息
 * 用于反馈非阻断性的操作结果（如：数量已调整、未发生变化）
 */
export interface CartNotice {
  type: 'CAPPED_APPLIED' | 'NO_CHANGE';
  message: string;
  context?: Record<string, any>;
  timestamp: number;
}


/**
 * 购物车核心数据状态
 * V4: 双层状态架构
 */
interface CartDataState {
  /** V4新增: 服务器权威快照 (不直接被 UI 读取) */
  serverCart: CartSnapshot | null;
  /** V4新增: UI渲染快照 (所有 UI 组件读取此状态) */
  uiCart: CartSnapshot;
  /** V4新增: 用户交互状态跟踪 */
  interaction: InteractionState;

  // ===== 兼容层字段 (直接映射到 uiCart,保持向后兼容) =====
  /** 商品总件数 (映射自 uiCart.count) */
  count: number;
  /** 货币代码 (映射自 uiCart.currency) */
  currency: string;
  /** 购物车行项目列表 (映射自 uiCart.items) */
  items: CartItem[];
  /** 行项目顺序索引 (用于稳定渲染顺序) */
  itemLineRefs: string[];
  /** 行项目索引表 (用于细粒度订阅) */
  itemsByLineRef: Record<string, CartItem>;
  /** 购物车层级的折扣应用 (映射自 uiCart.cart_level_discount_applications) */
  cart_level_discount_applications: DiscountApplication[];
  /** 总折扣金额 (映射自 uiCart.total_discount) */
  total_discount: number;
  /** 原始总价 (映射自 uiCart.original_total_price) */
  original_total_price: number;
  /** 最终支付金额 (映射自 uiCart.total_price) */
  total_price: number;

  // ===== 配置字段 (非双层) =====
  /** 免邮起始阈值 (分) */
  freeShippingThreshold: number;
  /** 基础政策配置 */
  config: CartPolicyConfig;
  /** 节假日配置 */
  holidays: HolidayConfig;
  /** 折扣/优惠券状态 */
  discountCode: DiscountCodeState;
}


interface CartUIState {
  isCartOpen: boolean;
  isLoading: boolean;
  isDiscountPanelOpen: boolean;
}

interface CartActions {
  // ===== V4 Core Actions (双层状态) =====
  setCart: (data: CartData, meta?: { source?: string }) => void;

  // V4 Interaction Tracking
  touchUiEdit: () => void;
  incrementActiveEditors: () => void;
  decrementActiveEditors: () => void;
  setUiItemQuantity: (lineRef: string, qty: number) => void;
  isUserInteracting: () => boolean;

  // ===== Legacy/Preserved Actions =====
  setLoading: (loading: boolean) => void;
  setCartFetchedAt: (ts: number) => void;

  // Discount Code Actions
  setDiscountCodeState: (state: Partial<DiscountCodeState>) => void;
  resetDiscountCode: () => void;


  // UI Actions
  toggleCart: (isOpen?: boolean) => void;
  toggleDiscountPanel: (isOpen?: boolean) => void;
  setFreeShippingThreshold: (amount: number) => void;

  // Cart Sync Actions
  setCartSyncStatus: (status: CartSyncStatus, error?: AppError | null) => void;

}

interface CartInternalState {
  lastCartFetchedAt: number;
  cartSyncStatus: CartSyncStatus;
  cartSyncError: AppError | null;
  cartNotices: CartNotice[];
}

// 合并类型
type CartState = CartDataState & CartUIState & CartActions & CartInternalState & {
  addCartNotice: (notice: Omit<CartNotice, 'timestamp'>) => void;
  clearCartNotices: () => void;
};

const DEFAULT_DISCOUNT_CODE_STATE: DiscountCodeState = {
  code: null,
  appliedCodes: [],
  isValid: false,
  discountAmount: 0,
  error: null,
  isValidating: false
};

// V4: 空购物车快照
const EMPTY_CART_SNAPSHOT: CartSnapshot = {
  count: 0,
  currency: 'EUR',
  items: [],
  cart_level_discount_applications: [],
  discount_codes: [],
  total_discount: 0,
  original_total_price: 0,
  total_price: 0
};

const CART_PERSIST_VERSION = 2;
const INTERACTION_GRACE_MS = 600;

type CartSyncStatus = 'synced' | 'syncing' | 'degraded';

/**
 * 全局购物车 Store（Zustand）：
 * - 持久化权威购物车数据
 * - 管理 UI/乐观状态
 * - 提供简明动作供 Service/UI 调用
 */
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      // ===== V4: 双层状态核心字段 =====
      serverCart: null,
      uiCart: EMPTY_CART_SNAPSHOT,
      interaction: {
        lastUiEditAt: 0,
        activeEditors: 0
      },

      // ===== 兼容层字段 (映射自 uiCart) =====
      count: 0,
      currency: 'EUR',
      items: [],
      itemLineRefs: [],
      itemsByLineRef: {},
      cart_level_discount_applications: [],
      total_discount: 0,
      original_total_price: 0,
      total_price: 0,

      // ===== 配置字段 =====
      freeShippingThreshold: getFreeShippingThresholdFromWindow(),
      config: getCartPolicyConfigFromWindow(),  // 从 Data Layer 初始化
      holidays: getHolidayConfigFromWindow(),   // 从 Data Layer 初始化节假日配置
      discountCode: DEFAULT_DISCOUNT_CODE_STATE,

      // =========== 2. UI 区域 (刷新即重置) ===========
      isCartOpen: false, // 默认关闭
      isLoading: false,
      isDiscountPanelOpen: false,
      lastCartFetchedAt: 0,
      cartSyncStatus: 'synced',
      cartSyncError: null,
      cartNotices: [],

      // =========== 3. Actions 区域 ===========
      addCartNotice: (notice) => set((state) => ({
        cartNotices: [...state.cartNotices, { ...notice, timestamp: Date.now() }]
      })),

      clearCartNotices: () => set({ cartNotices: [] }),

      setLoading: (isLoading) => set({ isLoading }),

      setDiscountCodeState: (patch) => set((state) => ({
        discountCode: { ...state.discountCode, ...patch }
      })),

      resetDiscountCode: () => set({ discountCode: DEFAULT_DISCOUNT_CODE_STATE }),

      toggleCart: (isOpen) => set((state) => ({
        isCartOpen: isOpen !== undefined ? isOpen : !state.isCartOpen
      })),

      toggleDiscountPanel: (isOpen) => set((state) => ({
        isDiscountPanelOpen: isOpen !== undefined ? isOpen : !state.isDiscountPanelOpen
      })),

      setFreeShippingThreshold: (amount) => set({ freeShippingThreshold: amount }),

      setCartSyncStatus: (status, error) => set({
        cartSyncStatus: status,
        cartSyncError: error ?? null
      }),


      // ===== V4 Core Actions Implementation =====

      /**
       * V4 统一入口: 写入购物车数据,执行对账逻辑
       * @param data - CartData from /cart.js
       * @param meta - 可选元数据 (source 等)
       */
      setCart: (data, meta) => set((state) => {
        const serverCart: CartSnapshot = {
          count: data.count,
          currency: data.currency,
          items: data.items,
          cart_level_discount_applications: data.cart_level_discount_applications,
          discount_codes: data.discount_codes,
          total_discount: data.total_discount,
          original_total_price: data.original_total_price,
          total_price: data.total_price
        };
        const appliedCodes = serverCart.discount_codes
          .filter((entry) => entry.code && entry.code !== '0' && entry.applicable)
          .map((entry) => entry.code.trim())
          .filter(Boolean);

        const interacting = get().isUserInteracting();
        const reconciledUiBase = reconcileCart(state.uiCart, serverCart, interacting);
        const forceSyncDiscounts = meta?.source?.startsWith('discount');
        const reconciledUi = forceSyncDiscounts
          ? {
            ...reconciledUiBase,
            cart_level_discount_applications: serverCart.cart_level_discount_applications,
            discount_codes: serverCart.discount_codes,
            total_discount: serverCart.total_discount,
            original_total_price: serverCart.original_total_price,
            total_price: serverCart.total_price
          }
          : reconciledUiBase;
        if (forceSyncDiscounts) {
          console.log('[cartStore] discount sync', {
            ui: state.uiCart.cart_level_discount_applications,
            server: serverCart.cart_level_discount_applications
          });
        }

        const { itemLineRefs, itemsByLineRef } = buildItemIndex(reconciledUi.items);

        return {
          serverCart,
          uiCart: reconciledUi,
          discountCode: {
            ...state.discountCode,
            appliedCodes
          },
          // 映射兼容字段到 uiCart
          count: reconciledUi.count,
          currency: reconciledUi.currency,
          items: reconciledUi.items,
          itemLineRefs,
          itemsByLineRef,
          cart_level_discount_applications: reconciledUi.cart_level_discount_applications,
          total_discount: reconciledUi.total_discount,
          original_total_price: reconciledUi.original_total_price,
          total_price: reconciledUi.total_price
        };
      }),

      /**
       * V4: 标记用户刚进行了 UI 编辑
       */
      touchUiEdit: () => set((state) => ({
        interaction: {
          ...state.interaction,
          lastUiEditAt: Date.now()
        }
      })),

      /**
       * V4: 增加活跃编辑器计数 (输入框聚焦)
       */
      incrementActiveEditors: () => set((state) => ({
        interaction: {
          ...state.interaction,
          activeEditors: state.interaction.activeEditors + 1
        }
      })),

      /**
       * V4: 减少活跃编辑器计数 (输入框失焦)
       */
      decrementActiveEditors: () => set((state) => ({
        interaction: {
          ...state.interaction,
          activeEditors: Math.max(0, state.interaction.activeEditors - 1)
        }
      })),

      /**
       * V4: 立即更新 uiCart 中某行的数量 (用于即时 UI 反馈)
       * @param lineRef - __clientLineId 或 item.key
       * @param qty - 新数量
       */
      setUiItemQuantity: (lineRef, qty) => set((state) => {
        const newUiCart = recalcUiCartAfterQtyChange({
          uiCart: state.uiCart,
          lineRef,
          nextQty: qty
        });

        const { itemLineRefs, itemsByLineRef } = buildItemIndex(newUiCart.items);

        return {
          uiCart: newUiCart,
          // 映射兼容字段
          items: newUiCart.items,
          itemLineRefs,
          itemsByLineRef,
          count: newUiCart.count,
          total_price: newUiCart.total_price,
          original_total_price: newUiCart.original_total_price,
          total_discount: newUiCart.total_discount
        };
      }),

      isUserInteracting: () => {
        const { interaction } = get();
        return (
          interaction.activeEditors > 0 ||
          Date.now() - interaction.lastUiEditAt < INTERACTION_GRACE_MS
        );
      },

      setCartFetchedAt: (ts) => set({ lastCartFetchedAt: ts })
    }),
    {
      name: 'cart-storage', // localStorage key
      version: CART_PERSIST_VERSION,
      migrate: (persistedState, version) => {
        const state = (persistedState as Partial<CartState>) || {};

        if (typeof version !== 'number' || version < CART_PERSIST_VERSION) {
          return {
            freeShippingThreshold: state.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS,
            config: state.config ?? getCartPolicyConfigFromWindow(),
            holidays: state.holidays ?? getHolidayConfigFromWindow()
          };
        }

        return {
          freeShippingThreshold: state.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS,
          config: state.config ?? getCartPolicyConfigFromWindow(),
          holidays: state.holidays ?? getHolidayConfigFromWindow()
        };
      },

      // 🔥【核心关键点】🔥
      // 仅持久化可复用的配置类数据，权威购物车数据由 /cart.js 驱动
      partialize: (state) => ({
        freeShippingThreshold: state.freeShippingThreshold,
        config: state.config,
        holidays: state.holidays
      }),
    }
  )
);

function buildItemIndex(items: CartItem[]) {
  const itemLineRefs: string[] = [];
  const itemsByLineRef: Record<string, CartItem> = {};

  items.forEach((item) => {
    const lineRef = getLineRef(item);
    itemLineRefs.push(lineRef);
    itemsByLineRef[lineRef] = item;
  });

  return { itemLineRefs, itemsByLineRef };
}
